import { useMemo, type FC, useContext, useEffect, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContainer, MainContent } from 'app/components/MainContent';
import Sidebar from 'app/components/Sidebar';
import { useRessource } from 'app/utils/ressource';
import { useTitle } from 'app/hooks';
import ErrorView from 'app/common/ErrorView';
import EventsSidebar from 'app/pages/EventsPage/EventsSidebar';
import { createFetchCompetitions, getFilteredAndSortedEventTree } from 'app/pages/EventsPage/utils';
import UserContext from 'app/common/UserContext';
import { useObservable } from 'app/hooks/useObservable';
import { isInView } from 'app/utils/dom';
import { useScrollEnd } from 'app/hooks/useScrollEnd';

import MonitoringFilterContext, { MonitoringFilterProvider } from './MonitoringFilterContext';
import { createFirstMonitoringRequest, handleMonitoringMessage } from './resources';
import MonitoredMarketCard from './MonitoredMarketCard';
import MonitoringFilters from './MonitoringFilters';
import style from './style.scss';

const MonitoringPage: FC = () => {
  const { t } = useTranslation();
  useTitle(t('monitoring-title'));
  const listRef = useRef<HTMLDivElement | null>(null);

  const { filters, setFilter, setSelectedTournaments, resetFilters } =
    useContext(MonitoringFilterContext);
  const { currentUser, preferences } = useContext(UserContext);

  const fetchCompetitions = useMemo(
    () => createFetchCompetitions(currentUser?.company.videogames),
    []
  );
  const {
    status: treeStatus,
    data: competitionsData,
    withLoader: withCompetitionLoader
  } = useObservable(fetchCompetitions);
  const isLoadingTree = ['NOT_STARTED', 'OBSERVING'].includes(treeStatus) && withCompetitionLoader;

  useEffect(() => {
    if (filters.selectedTournaments.videogames.length === 0 && competitionsData) {
      setSelectedTournaments(
        getFilteredAndSortedEventTree(competitionsData, preferences.ebattles_display)
      );
    }
  }, [filters.selectedTournaments, competitionsData, preferences.ebattles_display]);

  useEffect(() => {
    if (competitionsData) {
      setSelectedTournaments(
        getFilteredAndSortedEventTree(competitionsData, preferences.ebattles_display)
      );
    }
  }, [preferences.ebattles_display]);

  const firstPageRequest = useMemo(() => createFirstMonitoringRequest(filters, 1), []);

  const { data, pushMessage, withLoader, status, restart } = useRessource({
    fetchInitialData: firstPageRequest,
    topics: [{ topic: 'risk_score_monitoring' }, { topic: 'competitors' }],
    handleMessage: handleMonitoringMessage,
    messagesBufferTime: 2000,
    bufferMessageType: ['monitored_market', 'competitor'],
    fromCompetitors: true
  });

  const loadPage = (page: number) => {
    pushMessage({
      type: 'ui_event',
      action: 'load_page',
      page
    });
  };

  const loadNextPage = () => {
    pushMessage({
      type: 'ui_event',
      action: 'load_next_page'
    });
  };

  useEffect(() => {
    pushMessage({
      type: 'ui_event',
      action: 'refresh',
      filters
    });
  }, [filters, pushMessage]);

  useScrollEnd(listRef.current, loadNextPage);
  const pagesInViewRef = useRef([true]);

  useLayoutEffect(() => {
    if (listRef.current) {
      const listRefCurrent = listRef.current;

      const handleScroll = () => {
        const pagesInViewRefCurrent = pagesInViewRef.current;
        const pagesInView = [...(listRefCurrent.children ?? [])].map(item =>
          isInView(listRefCurrent, item as HTMLElement, true)
        );

        // If there's a change in numbers of pages, the data loading has already been handle by the useScrollEnd hook
        if (pagesInViewRefCurrent.length === pagesInView.length) {
          // We "load" the next/prev page only when there were 2 pages in view, and now there's only one
          if (
            pagesInView.filter(Boolean).length === 1 &&
            pagesInViewRefCurrent.filter(Boolean).length === 2
          ) {
            loadPage(pagesInView.indexOf(true) + 1);
          }
        }

        pagesInViewRef.current = pagesInView;
      };

      listRefCurrent.addEventListener('scroll', handleScroll);
      return () => {
        if (listRefCurrent) {
          listRefCurrent.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [listRef.current]);

  return (
    <AppContainer>
      <Sidebar collapsable>
        {({ collapsed }) => (
          <EventsSidebar
            collapsed={collapsed}
            loading={isLoadingTree}
            value={filters.selectedTournaments}
            onChange={setSelectedTournaments}
          />
        )}
      </Sidebar>
      <MainContent title={t('monitoring-title')} contentClassName={style.mainContent}>
        <div className={style.container}>
          <div className={style.containerContent}>
            <MonitoringFilters
              onValueChange={setFilter}
              values={filters}
              resetFilters={resetFilters}
              numberOfHiddenMarkets={data?.hiddenMarkets}
            />
            {(() => {
              if (withLoader) {
                return null;
              }
              if (status === 'FAILED') {
                return <ErrorView onRetry={restart} pandaUser />;
              }
              if (data) {
                if (data.pages.flat().length) {
                  return (
                    <div className={style.cardList} ref={listRef}>
                      {data.pages.map((page, index) => (
                        <div key={index} className={style.page}>
                          {page.map(market => (
                            <MonitoredMarketCard
                              key={market.market_ref}
                              market={market}
                              now={Date.now()}
                              hidden={filters.showHidden}
                              competitorsStatus={data.competitorsStatus}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                }
                return (
                  <div className={style.emptyView}>
                    <img src="images/all_good.png" alt="all good" />
                    <div className={style.emptyViewTitle}>{t('monitoring-empty-title')}</div>
                    <div>{t('monitoring-empty-description')}</div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </div>
      </MainContent>
    </AppContainer>
  );
};

MonitoringPage.displayName = 'MonitoringPage';

const MonitoringPageContainer: FC = () => (
  <MonitoringFilterProvider>
    <MonitoringPage />
  </MonitoringFilterProvider>
);

MonitoringPageContainer.displayName = 'MonitoringPageContainer';

export default MonitoringPageContainer;
